import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Save, Type, Eye, EyeOff, Megaphone, Globe } from "lucide-react";
import { AdminLayout } from "./AdminLayout";

/**
 * Admin Appearance & Markets — Sprint 36 (Control Center v2).
 *
 * Consolidated typed settings editor covering:
 *   • Homepage section titles + descriptions
 *   • Typography (font family, hero/section/body sizes) — enums only
 *   • Homepage section visibility + display order
 *   • Platform announcement banner
 *   • Market status (UAE only — other markets are display-only)
 *
 * All values are validated server-side via the typed key registry in
 * `backend/routers/settings_router.py`. No raw CSS. No arbitrary keys.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const readJson = async (r) => {
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
};

const FONT_LABELS = { cairo: "Cairo (افتراضي)", tajawal: "Tajawal", ibm_plex_arabic: "IBM Plex Arabic" };
const SIZE_LABELS = { sm: "صغير", md: "عادي", lg: "كبير", xl: "كبير جداً" };
const MARKET_STATUS_LABELS = {
  proof: "وضع الإثبات",
  active: "نشط",
  coming_soon: "قريباً",
  disabled: "معطّل",
};
const TONE_LABELS = { info: "معلومة", success: "نجاح", warning: "تحذير" };

const GROUPS = [
  {
    id: "typography",
    title: "الخطوط والأحجام",
    Icon: Type,
    keys: [
      "typography_font_family",
      "typography_hero_size",
      "typography_section_size",
      "typography_body_size",
    ],
  },
  {
    id: "sections",
    title: "إظهار وإخفاء الأقسام",
    Icon: Eye,
    keys: [
      "section_kpi_visible", "section_kpi_order",
      "section_opportunities_visible", "section_opportunities_order",
      "section_knowledge_visible", "section_knowledge_order",
    ],
  },
  {
    id: "announcement",
    title: "إعلان المنصّة",
    Icon: Megaphone,
    keys: [
      "announcement_enabled",
      "announcement_tone",
      "announcement_title",
      "announcement_body",
    ],
  },
  {
    id: "market",
    title: "حالة الأسواق",
    Icon: Globe,
    keys: ["market_ae_status"],
  },
];

const KEY_LABELS = {
  typography_font_family:  "عائلة الخط",
  typography_hero_size:    "حجم عنوان الهيرو",
  typography_section_size: "حجم عناوين الأقسام",
  typography_body_size:    "حجم النص الأساسي",
  section_kpi_visible:            "قسم مؤشّرات الأداء — ظاهر",
  section_kpi_order:              "قسم مؤشّرات الأداء — الترتيب",
  section_opportunities_visible:  "قسم الفرص — ظاهر",
  section_opportunities_order:    "قسم الفرص — الترتيب",
  section_knowledge_visible:      "قسم مكتبة المعرفة — ظاهر",
  section_knowledge_order:        "قسم مكتبة المعرفة — الترتيب",
  announcement_enabled: "الإعلان مُفعّل",
  announcement_tone:    "نغمة الإعلان",
  announcement_title:   "عنوان الإعلان",
  announcement_body:    "نص الإعلان",
  market_ae_status:     "الإمارات",
};

const ENUM_LABELS = {
  typography_font_family: FONT_LABELS,
  typography_hero_size: SIZE_LABELS,
  typography_section_size: SIZE_LABELS,
  typography_body_size: SIZE_LABELS,
  announcement_tone: TONE_LABELS,
  market_ae_status: MARKET_STATUS_LABELS,
};

const OTHER_MARKETS = [
  { code: "SA", label: "السعودية",  status: "coming_soon" },
  { code: "EG", label: "مصر",       status: "coming_soon" },
  { code: "QA", label: "قطر",       status: "coming_soon" },
  { code: "KW", label: "الكويت",    status: "coming_soon" },
  { code: "MA", label: "المغرب",    status: "coming_soon" },
];

export default function AdminAppearancePage() {
  const [specs, setSpecs] = useState([]);
  const [values, setValues] = useState({});
  const [initial, setInitial] = useState({});
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/settings`, { credentials: "include" });
      const data = await readJson(r);
      setSpecs(data);
      const shape = {};
      data.forEach((row) => { shape[row.key] = row.value || row.default || ""; });
      setValues(shape);
      setInitial(shape);
    } catch (e) {
      toast.error("تعذّر تحميل الإعدادات", { description: String(e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (key) => {
    setPending(key);
    try {
      const r = await fetch(`${API_BASE}/api/admin/settings/${key}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value: String(values[key] ?? "") }),
      });
      await readJson(r);
      setInitial((s) => ({ ...s, [key]: values[key] }));
      toast.success("تم الحفظ", { description: KEY_LABELS[key] || key });
    } catch (e) {
      toast.error("تعذّر الحفظ", { description: String(e) });
    } finally {
      setPending(null);
    }
  };

  const specByKey = useMemo(() => {
    const m = {};
    specs.forEach((s) => { m[s.key] = s; });
    return m;
  }, [specs]);

  const renderField = (key) => {
    const spec = specByKey[key];
    if (!spec) return null;
    const dirty = String(values[key] ?? "") !== String(initial[key] ?? "");
    const busy = pending === key;
    const enumMap = ENUM_LABELS[key] || {};

    let control = null;
    if (spec.type === "enum") {
      control = (
        <select
          disabled={loading}
          value={values[key] ?? spec.default}
          onChange={(e) => setValues((s) => ({ ...s, [key]: e.target.value }))}
          data-testid={`admin-appearance-input-${key}`}
          className="mt-2 h-10 w-full rounded-lg border border-surface-200 bg-white px-3 text-sm outline-none hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
        >
          {(spec.values || []).map((v) => (
            <option key={v} value={v}>{enumMap[v] || v}</option>
          ))}
        </select>
      );
    } else if (spec.type === "bool") {
      const on = String(values[key] ?? spec.default) === "true";
      control = (
        <button
          type="button"
          data-testid={`admin-appearance-input-${key}`}
          onClick={() => setValues((s) => ({ ...s, [key]: on ? "false" : "true" }))}
          className={`mt-2 inline-flex h-10 items-center gap-2 rounded-lg border px-4 text-sm font-medium transition-colors ${
            on ? "border-emerald-300 bg-emerald-50 text-emerald-800"
               : "border-surface-200 bg-white text-ink-700 hover:border-surface-300"
          }`}
        >
          {on ? <Eye className="h-4 w-4" strokeWidth={1.75} /> : <EyeOff className="h-4 w-4" strokeWidth={1.75} />}
          {on ? "ظاهر" : "مخفي"}
        </button>
      );
    } else if (spec.type === "int") {
      control = (
        <input
          type="number"
          min={spec.min ?? 1}
          max={spec.max ?? 99}
          disabled={loading}
          value={values[key] ?? spec.default}
          onChange={(e) => setValues((s) => ({ ...s, [key]: e.target.value }))}
          data-testid={`admin-appearance-input-${key}`}
          className="mt-2 h-10 w-full rounded-lg border border-surface-200 bg-white px-3 text-sm outline-none hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
        />
      );
    } else {
      // text
      const long = (spec.max ?? 200) > 120;
      control = long ? (
        <textarea
          value={values[key] ?? ""}
          disabled={loading}
          maxLength={spec.max}
          onChange={(e) => setValues((s) => ({ ...s, [key]: e.target.value }))}
          data-testid={`admin-appearance-input-${key}`}
          className="mt-2 min-h-20 w-full rounded-lg border border-surface-200 bg-white p-3 text-sm outline-none hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
        />
      ) : (
        <input
          type="text"
          value={values[key] ?? ""}
          disabled={loading}
          maxLength={spec.max}
          onChange={(e) => setValues((s) => ({ ...s, [key]: e.target.value }))}
          data-testid={`admin-appearance-input-${key}`}
          className="mt-2 h-10 w-full rounded-lg border border-surface-200 bg-white px-3 text-sm outline-none hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
        />
      );
    }

    return (
      <div
        key={key}
        data-testid={`admin-appearance-field-${key}`}
        className="rounded-lg border border-surface-200 bg-white p-4"
      >
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-ink-900">{KEY_LABELS[key] || key}</label>
          <span className="font-mono text-[10px] text-ink-500">{key}</span>
        </div>
        {control}
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[10px] text-ink-500">
            {spec.type === "text" ? `${(values[key] || "").length} / ${spec.max}` : `type: ${spec.type}`}
          </span>
          <button
            type="button"
            onClick={() => save(key)}
            disabled={!dirty || busy}
            data-testid={`admin-appearance-save-${key}`}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-fursa-700 px-4 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-fursa-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" strokeWidth={1.75} />
            {busy ? "جارٍ الحفظ…" : "حفظ"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout>
      <section className="space-y-6" data-testid="admin-appearance-root">
        <header className="space-y-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-fursa-700">
            Control Center · Appearance & Markets
          </p>
          <h2 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
            المظهر والأسواق
          </h2>
          <p className="max-w-2xl text-sm leading-relaxed text-ink-500">
            التحكّم في الخطوط، إظهار الأقسام، إعلانات المنصّة، وحالة سوق الإمارات.
            كل القيم محدودة ومُتحقّقة من الخادم — لا يُسمح بأي HTML أو CSS مخصص.
          </p>
        </header>

        {GROUPS.map((g) => (
          <div key={g.id} data-testid={`admin-appearance-group-${g.id}`} className="rounded-xl border border-surface-200 bg-surface-50 p-4 shadow-card">
            <div className="mb-3 flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white text-intel-800 shadow-sm">
                <g.Icon className="h-4 w-4" strokeWidth={1.75} />
              </span>
              <h3 className="text-sm font-semibold text-ink-900">{g.title}</h3>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {g.keys.map((k) => renderField(k))}
            </div>
            {g.id === "market" && (
              <div className="mt-3 rounded-lg border border-dashed border-surface-300 bg-white p-3">
                <p className="mb-2 text-[11px] font-semibold text-ink-500">أسواق أخرى مُهيّأة (عرض فقط — غير مُفعّلة علنياً)</p>
                <ul className="grid grid-cols-2 gap-2 md:grid-cols-3">
                  {OTHER_MARKETS.map((m) => (
                    <li
                      key={m.code}
                      data-testid={`admin-market-locked-${m.code}`}
                      className="flex items-center justify-between rounded-md bg-surface-50 px-3 py-2 text-xs"
                    >
                      <span className="font-medium text-ink-900">{m.label}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">
                        {MARKET_STATUS_LABELS[m.status]}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </section>
    </AdminLayout>
  );
}
